/**
 * ThemePreviewPicker — three large radio-cards, each rendering a mini
 * SaaS chrome mockup in its target theme.
 *
 * Why mockups (not just labels): users picking a theme want to *see*
 * what they're picking before committing. The text+icon segmented
 * control we ship in the topbar is fine for a quick toggle, but on a
 * dedicated preferences surface a full preview earns its space.
 *
 * How the previews stay theme-locked regardless of the document theme:
 * each preview tile sets its own CSS variables INLINE
 * (`style={{ '--paper': '#…', '--ink': '#…', … }}`), and the inner
 * Tailwind utilities resolve those vars locally. So the Light tile
 * always paints in light tokens even if the document is in dark mode,
 * and vice versa. The "System" tile is a diagonal split — left half
 * uses light tokens, right half uses dark — so the preview literally
 * shows what the OS would deliver.
 *
 * Active state: a 2-px sky ring + a check chip in the top-right. The
 * card itself is the radio target (label + invisible input), so the
 * keyboard path is the standard `space` to toggle, `tab` to move.
 */
import { Check, Sun, Moon, Monitor } from "lucide-react";
import { cn } from "@flint/ui";
import { useTheme, type ThemePreference } from "../workspace/theme/index.ts";

// ── Token snapshots ───────────────────────────────────────────────
// Source of truth: `packages/ui/src/tokens.css :root` (light) and
// `:root.dark` (dark). When you change those, mirror them here. They
// only have to match the FOUR variables the mockup uses (paper, bg,
// ink, sky-600) plus the `--line` for the chrome border.
const LIGHT_VARS: React.CSSProperties = {
  ["--paper" as string]: "#FFFFFF",
  ["--bg" as string]: "#F4F6F9",
  ["--ink" as string]: "#0F172A",
  ["--ink-2" as string]: "#1E293B",
  ["--muted" as string]: "#64748B",
  ["--sky-600" as string]: "#0284C7",
  ["--sky-100" as string]: "#E0F2FE",
  ["--line" as string]: "#E2E8F0",
  ["--slate-700" as string]: "#334155",
};

const DARK_VARS: React.CSSProperties = {
  ["--paper" as string]: "#0F172A",
  ["--bg" as string]: "#020617",
  ["--ink" as string]: "#F1F5F9",
  ["--ink-2" as string]: "#E2E8F0",
  ["--muted" as string]: "#94A3B8",
  ["--sky-600" as string]: "#0EA5E9",
  ["--sky-100" as string]: "#075985",
  ["--line" as string]: "#1E293B",
  ["--slate-700" as string]: "#475569",
};

// ── Preview tile internals ────────────────────────────────────────
/** A miniature SaaS-chrome mock — sidebar, topbar, content card. */
function ChromeMock() {
  return (
    <div
      className="h-full w-full rounded-[5px] overflow-hidden flex"
      // Background is the workspace bg; the elevated surface is paper.
      style={{ backgroundColor: "var(--bg)" }}
    >
      {/* Sidebar — slate spine matches the real app's sidebar bg. */}
      <div
        className="w-[18%] flex flex-col gap-1 p-1.5"
        style={{ backgroundColor: "var(--slate-700)" }}
      >
        <div
          className="h-1 rounded-[1px] w-2/3"
          style={{ backgroundColor: "var(--paper)", opacity: 0.9 }}
        />
        <div
          className="h-0.5 rounded-[1px] w-full"
          style={{ backgroundColor: "var(--paper)", opacity: 0.4 }}
        />
        <div
          className="h-0.5 rounded-[1px] w-3/4"
          style={{ backgroundColor: "var(--paper)", opacity: 0.25 }}
        />
        <div
          className="h-0.5 rounded-[1px] w-2/3"
          style={{ backgroundColor: "var(--paper)", opacity: 0.25 }}
        />
      </div>

      {/* Right column: topbar + body. */}
      <div className="flex-1 flex flex-col">
        {/* Topbar — paper with hairline. */}
        <div
          className="h-[14%] flex items-center gap-1 px-2 border-b"
          style={{ backgroundColor: "var(--paper)", borderColor: "var(--line)" }}
        >
          <div
            className="h-1 w-6 rounded-[1px]"
            style={{ backgroundColor: "var(--ink)", opacity: 0.7 }}
          />
        </div>

        {/* Body card — the page content. */}
        <div className="flex-1 p-1.5">
          <div
            className="h-full w-full rounded-[3px] border p-1.5 flex flex-col gap-1"
            style={{ backgroundColor: "var(--paper)", borderColor: "var(--line)" }}
          >
            <div className="h-1.5 w-1/2 rounded-[1px]" style={{ backgroundColor: "var(--ink)" }} />
            {/* Sky pill — the brand accent. */}
            <div
              className="h-1.5 w-1/4 rounded-[6px]"
              style={{ backgroundColor: "var(--sky-100)" }}
            >
              <div
                className="h-full rounded-[6px]"
                style={{ width: "60%", backgroundColor: "var(--sky-600)" }}
              />
            </div>
            <div className="flex gap-1 mt-0.5">
              <div
                className="h-0.5 flex-1 rounded-[1px]"
                style={{ backgroundColor: "var(--muted)", opacity: 0.6 }}
              />
              <div
                className="h-0.5 flex-1 rounded-[1px]"
                style={{ backgroundColor: "var(--muted)", opacity: 0.6 }}
              />
            </div>
            <div
              className="h-0.5 w-3/4 rounded-[1px]"
              style={{ backgroundColor: "var(--muted)", opacity: 0.5 }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tile shells ───────────────────────────────────────────────────
function LightPreview() {
  return (
    <div className="absolute inset-0" style={LIGHT_VARS}>
      <ChromeMock />
    </div>
  );
}

function DarkPreview() {
  return (
    <div className="absolute inset-0" style={DARK_VARS}>
      <ChromeMock />
    </div>
  );
}

/**
 * "System" preview — diagonal split. Left half renders the light mock,
 * right half the dark mock; the seam is a 45° clip-path so the eye
 * reads "follows your device" without needing a label.
 */
function SystemPreview() {
  return (
    <div className="absolute inset-0">
      {/* Light layer fills the full preview, then the dark layer on top
          is clipped to the right-bottom triangle. The clip-path uses
          a slightly off-vertical seam so it doesn't read as exactly
          half-and-half — it nods at "your OS picks". */}
      <div className="absolute inset-0" style={LIGHT_VARS}>
        <ChromeMock />
      </div>
      <div
        className="absolute inset-0"
        style={{ ...DARK_VARS, clipPath: "polygon(58% 0, 100% 0, 100% 100%, 42% 100%)" }}
      >
        <ChromeMock />
      </div>
    </div>
  );
}

// ── Options definition ────────────────────────────────────────────
interface Option {
  value: ThemePreference;
  label: string;
  Icon: typeof Sun;
  Preview: () => React.JSX.Element;
}

const OPTIONS: readonly Option[] = [
  { value: "light", label: "Light", Icon: Sun, Preview: LightPreview },
  { value: "dark", label: "Dark", Icon: Moon, Preview: DarkPreview },
  { value: "system", label: "System", Icon: Monitor, Preview: SystemPreview },
];

// ── Public component ──────────────────────────────────────────────
export interface ThemePreviewPickerProps {
  className?: string;
  /**
   * Tile sizing. `compact` (default) renders narrower tiles tuned for
   * the Profile section where the picker sits alongside other dense
   * controls. `roomy` keeps the original aspect ratio + active ring
   * for any future standalone surface.
   */
  size?: "compact" | "roomy";
}

export function ThemePreviewPicker({ className, size = "compact" }: ThemePreviewPickerProps) {
  const { preference, setPreference } = useTheme();
  const isCompact = size === "compact";

  return (
    <fieldset
      className={cn("grid grid-cols-3", isCompact ? "gap-2 max-w-md" : "gap-3", className)}
    >
      <legend className="sr-only">Theme preference</legend>
      {OPTIONS.map(({ value, label, Icon, Preview }) => {
        const isActive = preference === value;
        return (
          <label
            key={value}
            className={cn(
              "group relative flex flex-col rounded-md border bg-paper overflow-hidden cursor-pointer transition-all",
              "focus-within:outline-none focus-within:ring-2 focus-within:ring-sky-600 focus-within:ring-offset-2 focus-within:ring-offset-paper",
              isActive
                ? isCompact
                  ? "border-sky-600 ring-1 ring-sky-600 shadow-card"
                  : "border-sky-600 ring-2 ring-sky-600 ring-offset-2 ring-offset-paper shadow-card"
                : "border-line hover:border-line-2",
            )}
          >
            <input
              type="radio"
              name="theme-preference"
              value={value}
              checked={isActive}
              onChange={() => setPreference(value)}
              className="sr-only"
            />

            {/* Preview surface — `compact` uses a flatter aspect ratio so
                the tile reads as a small swatch rather than a hero card. */}
            <div className={cn("relative w-full bg-bg", isCompact ? "aspect-[2/1]" : "aspect-[5/3]")}>
              <Preview />
              {isActive ? (
                <span
                  className={cn(
                    "absolute inline-grid place-items-center rounded-full bg-sky-600 text-paper shadow-card",
                    isCompact ? "top-1 right-1 h-4 w-4" : "top-1.5 right-1.5 h-5 w-5",
                  )}
                  aria-hidden
                >
                  <Check size={isCompact ? 9 : 11} strokeWidth={3} />
                </span>
              ) : null}
            </div>

            <div
              className={cn(
                "flex items-center justify-center gap-1.5 border-t border-line",
                isCompact ? "py-1.5" : "py-2",
              )}
            >
              <Icon
                size={12}
                strokeWidth={2}
                aria-hidden
                className={isActive ? "text-sky-700" : "text-muted"}
              />
              <span className={cn("text-sm font-medium", isActive ? "text-ink" : "text-ink-2")}>
                {label}
              </span>
            </div>
          </label>
        );
      })}
    </fieldset>
  );
}
